import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { SUPPORTED_LANGUAGES } from "@/i18n";
import {
  LayoutDashboard,
  Building2,
  Users,
  GitCompareArrows,
  KanbanSquare,
  Receipt,
  FileSignature,
  Settings,
  Radar,
  Inbox,
  LogOut,
  Menu,
  X,
  Globe,
  CreditCard,
  ShieldCheck,
  ShieldAlert,
  UserCog,
  Wallet,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import NotificationCenter from "@/components/NotificationCenter";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TrialBanner from "@/components/TrialBanner";

const NAV = [
  { to: "/tableau-de-bord", key: "nav.dashboard", icon: LayoutDashboard, testid: "nav-dashboard", roles: ["superadmin", "client"] },
  { to: "/radar", key: "nav.radar", icon: Radar, testid: "nav-radar", roles: ["superadmin", "client"] },
  { to: "/abonnement", key: "nav.subscription", icon: CreditCard, testid: "nav-subscription", roles: ["client"] },
  { to: "/mon-compte", key: "nav.account", icon: UserCog, testid: "nav-account", roles: ["superadmin", "client"] },
  { to: "/admin", key: "nav.admin", icon: ShieldCheck, testid: "nav-admin", roles: ["superadmin"] },
  { to: "/admin/securite", key: "nav.security", icon: ShieldAlert, testid: "nav-security", roles: ["superadmin"] },
  { to: "/admin/couts", key: "nav.costs", icon: Wallet, testid: "nav-costs", roles: ["superadmin"] },
  { to: "/prospects", key: "nav.prospects", icon: Inbox, testid: "nav-prospects", roles: ["superadmin"] },
  { to: "/parametres-emetteur", key: "nav.issuerSettings", icon: Settings, testid: "nav-issuer-settings", roles: ["superadmin"] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState(i18n.language?.slice(0, 2) || "fr");

  const changeLang = (code) => {
    const target = SUPPORTED_LANGUAGES.find((l) => l.code === code);
    if (!target || !target.ready) return;
    i18n.changeLanguage(code); setLang(code);
  };
  const role = user?.role || "client";
  const navItems = NAV.filter((it) => (it.roles || ["superadmin", "client"]).includes(role));

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarContent = () => (
    <div className="flex h-full flex-col bg-[#0F172A] text-slate-300">
      <div className="px-7 py-8 border-b border-white/10">
        <div className="flex items-start justify-between">
          <div>
            <p data-testid="sidebar-brand" className="text-base font-bold uppercase tracking-[0.18em] text-white antialiased leading-snug">{t("app.overline")}</p>
          </div>
          {(role === "superadmin" || user?.radar_access) && <NotificationCenter />}
        </div>
      </div>
      <nav className="flex-1 px-4 py-6 space-y-1">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={item.testid}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors duration-200 ${
                isActive
                  ? "bg-[#C6A87C] text-[#0F172A]"
                  : "text-slate-400 hover:bg-white/5 hover:text-white"
              }`
            }
          >
            <item.icon className="h-[18px] w-[18px]" strokeWidth={1.8} />
            {t(item.key)}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 px-4 py-3">
        <div className="flex items-center gap-2" data-testid="lang-switcher">
          <Globe className="h-4 w-4 shrink-0 text-slate-500" />
          <Select value={lang} onValueChange={changeLang}>
            <SelectTrigger
              data-testid="lang-select-trigger"
              className="h-9 flex-1 rounded-lg border-white/15 bg-white/5 text-sm font-medium text-white hover:border-[#C6A87C]/60 focus:ring-[#C6A87C]/40"
            >
              <SelectValue placeholder={t("common.language")} />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {SUPPORTED_LANGUAGES.map((l) => (
                <SelectItem key={l.code} value={l.code} disabled={!l.ready} data-testid={`lang-${l.code}`}>
                  <span className="flex w-full items-center justify-between gap-3">
                    <span>{l.label}</span>
                    {!l.ready && <span className="text-[10px] italic text-slate-400">{t("common.comingSoon", "bientôt disponible")}</span>}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="border-t border-white/10 p-4">
        <div className="flex items-center gap-3 px-3 py-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#C6A87C] text-[#0F172A] text-sm font-bold">
            {(user?.name || user?.email || "A").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm text-white">{user?.name || t("nav.user")}</p>
            <p className="truncate text-xs text-slate-500">{user?.email}</p>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="mt-2 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-400 transition-colors hover:bg-white/5 hover:text-white"
        >
          <LogOut className="h-4 w-4" /> {t("nav.logout")}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#FAFAFA]">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 lg:block">
        <div className="fixed h-screen w-64">
          <SidebarContent />
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="fixed inset-x-0 top-0 z-40 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 lg:hidden">
        <span data-testid="mobile-brand" className="text-sm font-bold uppercase tracking-[0.14em] text-[#0F172A]">{t("app.overline")}</span>
        <div className="flex items-center gap-1">
          {(role === "superadmin" || user?.radar_access) && <NotificationCenter tone="light" />}
          <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)} className="text-slate-700">
            <Menu />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-72">
            <button
              onClick={() => setOpen(false)}
              className="absolute right-3 top-4 z-10 text-slate-400"
              data-testid="mobile-menu-close"
            >
              <X />
            </button>
            <SidebarContent />
          </div>
        </div>
      )}

      <main className="min-w-0 flex-1 pt-16 lg:pt-0">
        <TrialBanner />
        <Outlet />
      </main>
    </div>
  );
}
